import { Link } from "react-router-dom";
import { usePageMeta } from "@/hooks";
import { ComicViewer } from "@/components/ComicViewer";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { JsonLd } from "@/components/common/JsonLd";
import { buildFaqPage } from "@/lib/structuredData";
import { MessageSquare, CreditCard, Pencil, Send, FileText, HelpCircle } from "lucide-react";

const COMIC_PAGES = [
  {
    src: "/static/images/comics/comic-request.png",
    alt: "Step 1: The Request - Choose your artist and send references",
    label: "Part 1: The Request",
  },
  {
    src: "/static/images/comics/comic-preparing.png",
    alt: "Step 2: The Process - Payment, sketch review, and delivery",
    label: "Part 2: The Process",
  },
];

const PROCESS_STEPS = [
  {
    icon: MessageSquare,
    title: "1. Send Your Request",
    description: "Browse our artists, pick one you like, and send a request through our form, Etsy, or email. Include references and describe your vision!",
  },
  {
    icon: CreditCard,
    title: "2. Get Your Quote",
    description: "We'll review your request and send you a price quote within 24 hours. Once you approve, you'll receive a PayPal invoice.",
  },
  {
    icon: Pencil,
    title: "3. Review the Sketch",
    description: "Your artist creates a sketch for your approval. Changes are free at this stage! Once approved, they proceed to the final artwork.",
  },
  {
    icon: Send,
    title: "4. Receive Your Art",
    description: "Your finished artwork is delivered within 2 weeks. You'll receive high-resolution files ready for use!",
  },
];

const TOS_DATA = [
  {
    title: "General Terms",
    items: [
      "By ordering you agree to our T.O.S",
      "Artist retains ownership for promotional display",
      "Credit the artist and store when posting",
      "No AI or NFT affiliation with our art",
      "No editing or altering the received art",
      "No profits without commercial rights",
    ],
  },
  {
    title: "Payment",
    items: [
      "Payment upfront via PayPal invoice",
      "No refund after work has started",
      "No chargebacks under any circumstances",
      "We issue refunds if we can't complete",
      "Ask for price quote before committing",
      "Tips appreciated but not required!",
    ],
  },
  {
    title: "Process",
    items: [
      "Max 2 weeks for final result",
      "Sketch sent for review before coloring",
      "Free changes during sketch phase",
      "Waitlist available when slots are full",
      "Rush fee available for commercial projects",
      "You'll be notified of queue position",
    ],
  },
  {
    title: "Commercial Use",
    items: [
      "+50-100% of original commission cost",
      "Includes: streaming, merch, video games",
      "Includes: VTubing, monetized content",
      "Must credit artist in visible location",
      "Only applies to specified actions",
      "Outlined clearly in invoice",
    ],
  },
];

const FAQ_DATA = [
  {
    question: "How do I start a commission?",
    answer: "Browse our artists, pick one you like, and send a request through our contact form, Etsy, or email. Include references and describe what you want!",
  },
  {
    question: "How long does a commission take?",
    answer: "Most commissions are completed within 2 weeks. Complex pieces or busy periods may take longer. You'll always be notified of your queue position.",
  },
  {
    question: "What payment methods do you accept?",
    answer: "We accept PayPal invoices and Etsy checkout. Payment is required upfront before work begins.",
  },
  {
    question: "Can I request changes?",
    answer: "Yes! During the sketch phase, changes are free. After coloring begins, major changes may incur additional fees.",
  },
  {
    question: "Is the art made by real human artists?",
    answer: "Yes. Every piece is drawn by hand by a real artist — we don't use AI at any stage, from the first sketch to the final colors. It's written into our Terms of Service, and it's been that way since 2020.",
  },
  {
    question: "Do you draw NSFW content?",
    answer: "Some of our artists accept NSFW commissions. Check the artist's profile to see their NSFW status and preferences.",
  },
  {
    question: "Can I use the art commercially?",
    answer: "Commercial use requires an additional fee (+50-100%). This covers streaming, merchandise, games, VTubing, and other monetized uses.",
  },
  {
    question: "What if an artist is closed for commissions?",
    answer: "You can join the waitlist! We'll notify you when slots open up. Alternatively, check out other artists with similar styles.",
  },
  {
    question: "Why does StudioComm handle communication?",
    answer: "We translate requests into the artist's native language, ensuring clarity and preventing misunderstandings. This lets artists focus on creating!",
  },
];

export function HowItWorksPage() {
  usePageMeta({
    title: "How It Works — Commission Process | StudioComm",
    description:
      "How to commission hand-drawn art at StudioComm: pick an artist, send references, approve the sketch, receive your finished piece — typically within 2 weeks.",
    canonicalPath: "/how-it-works",
  });
  return (
    <div className="bg-[var(--color-bg)]">
      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[var(--color-primary)]/10 to-transparent" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="font-[var(--font-display)] text-4xl md:text-5xl text-[var(--color-text)] mb-6">
            How to <em className="text-[var(--color-primary)]">Commission</em>
          </h1>
          <p className="text-lg text-[var(--color-text-muted)] max-w-2xl mx-auto">
            Our mascot Komiko will guide you through the process. It's easier than you think!
          </p>
        </div>
      </section>

      {/* Comic Section */}
      <section className="py-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <span className="text-xs font-medium uppercase tracking-widest text-[var(--color-text-muted)] mb-2 block">
              The Process
            </span>
            <h2 className="text-2xl font-bold text-[var(--color-text)]">
              A Quick <em className="text-[var(--color-primary)]">Visual Guide</em>
            </h2>
          </div>
          <ComicViewer pages={COMIC_PAGES} />
        </div>
      </section>

      {/* Process Steps */}
      <section className="py-16 bg-[var(--color-bg-card)]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-xs font-medium uppercase tracking-widest text-[var(--color-text-muted)] mb-2 block">
              Step by Step
            </span>
            <h2 className="text-2xl font-bold text-[var(--color-text)]">
              The Commission <em className="text-[var(--color-primary)]">Process</em>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {PROCESS_STEPS.map((step, idx) => (
              <div
                key={idx}
                className="bg-[var(--color-bg)] rounded-xl p-6 border border-[var(--color-border)] hover:border-[var(--color-primary)] transition-colors"
              >
                <div className="w-12 h-12 rounded-full bg-[var(--color-primary)]/10 flex items-center justify-center mb-4">
                  <step.icon className="w-6 h-6 text-[var(--color-primary)]" />
                </div>
                <h3 className="text-lg font-semibold text-[var(--color-text)] mb-2">
                  {step.title}
                </h3>
                <p className="text-sm text-[var(--color-text-muted)]">
                  {step.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Terms of Service */}
      <section id="tos" className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 mb-4">
              <FileText className="w-6 h-6 text-[var(--color-primary)]" />
            </div>
            <h2 className="text-2xl font-bold text-[var(--color-text)]">
              Terms of <em className="text-[var(--color-primary)]">Service</em>
            </h2>
            <p className="text-[var(--color-text-muted)] mt-2">
              Please read before commissioning
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {TOS_DATA.map((category, idx) => (
              <div
                key={idx}
                className="bg-[var(--color-bg-card)] rounded-xl p-6 border border-[var(--color-border)]"
              >
                <h3 className="font-semibold text-[var(--color-text)] mb-4 text-lg">
                  {category.title}
                </h3>
                <ul className="space-y-2">
                  {category.items.map((item, itemIdx) => (
                    <li
                      key={itemIdx}
                      className="text-sm text-[var(--color-text-muted)] flex items-start gap-2"
                    >
                      <span className="text-[var(--color-primary)] mt-0.5">•</span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-16 bg-[var(--color-bg-card)]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 mb-4">
              <HelpCircle className="w-6 h-6 text-[var(--color-primary)]" />
            </div>
            <h2 className="text-2xl font-bold text-[var(--color-text)]">
              Frequently Asked <em className="text-[var(--color-primary)]">Questions</em>
            </h2>
          </div>

          <JsonLd data={buildFaqPage(FAQ_DATA)} />
          {/* Answers stay in the HTML (forceMount) so crawlers read them;
              closed panels are hidden from people as usual. */}
          <Accordion type="single" collapsible className="space-y-3 [&_[role=region][data-state=closed]]:hidden">
            {FAQ_DATA.map((faq, idx) => (
              <AccordionItem
                key={idx}
                value={`faq-${idx}`}
                className="bg-[var(--color-bg)] rounded-lg border border-[var(--color-border)] overflow-hidden"
              >
                <AccordionTrigger className="px-6 py-4 hover:no-underline hover:bg-[var(--color-bg-card)]/50 transition-colors text-left">
                  <span className="font-medium text-[var(--color-text)]">{faq.question}</span>
                </AccordionTrigger>
                <AccordionContent forceMount className="px-6 pb-4 text-[var(--color-text-muted)]">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl font-bold text-[var(--color-text)] mb-4">
            Ready to Get Started?
          </h2>
          <p className="text-[var(--color-text-muted)] mb-8 max-w-2xl mx-auto">
            Browse our talented artists and find the perfect match for your vision.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/#artists"
              className="inline-flex items-center justify-center h-12 px-8 rounded-full bg-[var(--color-primary)] text-[var(--color-bg)] font-semibold hover:bg-[var(--color-primary-light)] transition-colors"
            >
              Browse Artists
            </Link>
            <Link
              to="/#contact"
              className="inline-flex items-center justify-center h-12 px-8 rounded-full bg-[var(--color-bg-card)] border-2 border-[var(--color-border)] text-[var(--color-text)] font-medium hover:border-[var(--color-primary)] hover:text-[var(--color-primary)] transition-colors"
            >
              Start a Commission
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
